import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "../lib/utils";

interface HorizontalScrollerProps {
    children: ReactNode;
    className?: string;
    contentClassName?: string;
    scrollAmount?: number;
}

export default function HorizontalScroller({
    children,
    className,
    contentClassName,
    scrollAmount = 320,
}: HorizontalScrollerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const syncScrollState = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        const epsilon = 1;

        setCanScrollLeft(container.scrollLeft > epsilon);
        setCanScrollRight(container.scrollLeft < maxScrollLeft - epsilon);
    }, []);

    useEffect(() => {
        syncScrollState();

        const container = containerRef.current;
        if (!container) return;

        container.addEventListener("scroll", syncScrollState, { passive: true });
        window.addEventListener("resize", syncScrollState);

        return () => {
            container.removeEventListener("scroll", syncScrollState);
            window.removeEventListener("resize", syncScrollState);
        };
    }, [syncScrollState]);

    const scrollByAmount = (direction: "left" | "right") => {
        const container = containerRef.current;
        if (!container) return;

        const amount = direction === "left" ? -scrollAmount : scrollAmount;
        container.scrollBy({ left: amount, behavior: "smooth" });
    };

    const scrollMaskStyle = useMemo(() => {
        if (!canScrollRight) return undefined;

        const rightFadeWidth = "72px";
        return {
            WebkitMaskImage: `linear-gradient(to right, black 0%, black calc(100% - ${rightFadeWidth}), transparent 100%)`,
            maskImage: `linear-gradient(to right, black 0%, black calc(100% - ${rightFadeWidth}), transparent 100%)`,
        };
    }, [canScrollRight]);

    return (
        <div className={cn("relative group", className)}>
            <button
                type="button"
                aria-label="Scroll left"
                onClick={() => scrollByAmount("left")}
                disabled={!canScrollLeft}
                className={cn(
                    "absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white shadow-lg rounded-full p-2 transition-opacity -ml-4",
                    canScrollLeft ? "opacity-0 group-hover:opacity-100" : "opacity-0 pointer-events-none"
                )}
            >
                <ChevronLeft className="h-5 w-5" />
            </button>

            <div
                ref={containerRef}
                style={scrollMaskStyle}
                className={cn(
                    "flex items-stretch gap-4 overflow-x-auto pb-4 scrollbar-hide",
                    contentClassName,
                )}
            >
                {children}
            </div>

            <button
                type="button"
                aria-label="Scroll right"
                onClick={() => scrollByAmount("right")}
                disabled={!canScrollRight}
                className={cn(
                    "absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white shadow-lg rounded-full p-2 transition-opacity -mr-4",
                    canScrollRight ? "opacity-0 group-hover:opacity-100" : "opacity-0 pointer-events-none"
                )}
            >
                <ChevronRight className="h-5 w-5" />
            </button>
        </div>
    );
}
